import './eventsource-polyfill.js';

import {
  getOrganizationId,
  getSalesforceMessagingUrl,
  getJwt,
  getLastEventId,
} from './dataProvider';
import type { EventListenerMap } from './types.js';

interface CustomEventSource {
  [key: string]:
    | ((eventName: string, callback: (event: MessageEvent) => void) => void)
    | 0
    | 1
    | 2;
  close: () => void;
  onerror: (error: unknown) => void;
  onopen: () => void;
  readyState: 0 | 1 | 2;
}

declare global {
  interface Window {
    EventSourcePolyfill: {
      new (url: string, eventSourceParams: unknown): CustomEventSource;
    };
  }
}

/**
 * Instance of an EventSourcePolyfill object to add or remove event listeners on.
 */
export let eventSource: CustomEventSource | undefined;

/**
 * Get the request headers for connecting to SSE.
 */
const getEventSourceParams = () => {
  return {
    headers: {
      Authorization: 'Bearer ' + getJwt(),
      'Last-Event-ID': getLastEventId(),
      'X-Org-ID': getOrganizationId(),
    },
    heartbeatTimeout: 90000,
  };
};

/**
 * Add event listeners on an EventSourcePolyfill object.
 *
 * @param {String} listenerOperationName - Name of the operator to act on an instance of EventSourcePolyfill, either "addEventListener" or "removeEventListener".
 * @param {Object} eventListenerMap - Map of event names and corresponding handlers for server-sent events. Shape of object is { eventName: eventHandler }.
 */
const handleEventSourceListeners = (
  listenerOperationName: string,
  eventListenerMap: EventListenerMap,
) => {
  if (typeof eventSource !== 'object') {
    throw new Error(
      `Expected EventSourcePolyfill object to be a valid object, but received: ${eventSource}`,
    );
  }
  if (
    typeof listenerOperationName !== 'string' ||
    !listenerOperationName.length
  ) {
    throw new Error(
      `Expected a valid string to either add or remove event listeners, but received: ${listenerOperationName}`,
    );
  }

  Object.entries(eventListenerMap).forEach(entry => {
    const [eventName, eventHandler] = entry;

    if (typeof eventName !== 'string') {
      throw new Error(
        `Expected event listener name parameter to be a valid string, but received: ${eventName}.`,
      );
    }
    if (eventName.trim().length < 1) {
      throw new Error(
        `Expected event listener name parameter to be a string with length greater than 0, but received: ${eventName}.`,
      );
    }
    if (typeof eventHandler !== 'function') {
      throw new Error(
        `Expected event listener handler parameter to be a function, but received: ${eventHandler}.`,
      );
    }

    if (eventSource) {
      // Perform event listener operation (add or remove) on eventSource.
      // @ts-expect-error Types are bad, `readyState` property gets in the way
      eventSource[listenerOperationName]?.(eventName, eventHandler);
    }
  });
};

/**
 * Establish the EventSourcePolyfill object with handlers for onopen and onerror.
 *
 * @param {String} fullApiPath - The full API path endpoint to request server-sent events.
 * @param {Object} eventListenerMap - Map of event handlers for server-sent events. Shape of object is { eventName: eventHandler }.
 * @returns {Promise}
 */
const createEventSource = (
  fullApiPath: string,
  eventListenerMap: EventListenerMap,
) => {
  if (
    !window.EventSourcePolyfill ||
    typeof window.EventSourcePolyfill !== 'function'
  ) {
    throw new Error('EventSourcePolyfill is not a constructor.');
  }
  if (typeof fullApiPath !== 'string' || !fullApiPath.length) {
    throw new Error(
      `Expected full API path parameter to be a valid string, but received: ${fullApiPath}.`,
    );
  }
  if (typeof eventListenerMap !== 'object') {
    throw new Error(
      `Expected event listener map parameter to be a valid object map, but received: ${eventListenerMap}.`,
    );
  }

  /**
   * Create a closure here to isolate `reconnectAttempts` and `reconnectIntervalSeconds` values to a single invocation of `createEventSource`.
   * All calls to `resolveEventSource` within a call to `createEventSource` share the same `reconnectAttempts` and `reconnectIntervalSeconds` values.
   *
   * @param {Promise.resolve} resolve - Event source opened.
   * @param {Promise.reject} reject - Attempted to reconnect to event source too many times.
   */
  return new Promise<void>((resolve, reject) => {
    try {
      eventSource = new window.EventSourcePolyfill(
        fullApiPath,
        getEventSourceParams(),
      );

      eventSource.onopen = () => {
        handleEventSourceListeners('addEventListener', eventListenerMap);
        resolve();
      };
      eventSource.onerror = err => {
        reject(err);
      };
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Subscribe to EventSourcePolyfill by providing authorization and org details.
 *
 * @param {Object} eventListenerMap - Map of event handlers for server-sent events. Shape of object is { eventName: eventHandler }.
 * @returns {Promise}
 */
export const subscribeToEventSource = (eventListenerMap: EventListenerMap) => {
  const messagingUrl = getSalesforceMessagingUrl();

  if (!messagingUrl) {
    return Promise.reject(
      new Error(
        `Expected a valid Messaging URL to establish a connection to the Event Source, but instead received ${messagingUrl}`,
      ),
    );
  }

  return new Promise((resolve, reject) => {
    try {
      /**
       * Directly connect to event router endpoint on Salesforce Messaging domain instead of going through ia-message.
       */
      createEventSource(
        getSalesforceMessagingUrl().concat(
          `/eventrouter/v1/sse?_ts=${Date.now()}`,
        ),
        eventListenerMap,
      )
        .then(resolve, (error: unknown) => {
          /**
           * If this reject function is called, there are likely three possibilities:
           * 1. a syntactic error in creating the EventSourcePolyfill
           * 2. too many reconnect attempts
           * 3. JWT has expired
           */
          reject(error);
        })
        .catch((error: unknown) => {
          reject(error);
        });
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Close the event source to end event stream.
 * @returns {Promise}
 */
export const closeEventSource = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      if (eventSource) {
        eventSource.close();
      }
      resolve();
    } catch (error) {
      reject(error);
    }
  });
};
